<script setup lang="ts">
import { ref, computed, onMounted, h } from 'vue'

import { useRoute, useRouter } from 'vue-router'

import { Icon } from '@iconify/vue'
import { onClickOutside } from '@vueuse/core'

import CompanyOrganizationSelector from '@/components/frontend/company-organization/CompanyOrganizationSelector.vue'
import ProfilePanelDialogContent from '@/components/frontend/profile/ProfilePanelDialogContent.vue'
import SVGIcon from '@/components/UI/SVGIcon.vue'
import UserAvatar from '@/components/UI/UserAvatar.vue'

import { useConfirmDialog } from '@/composables/useConfirmDialog'
import { useOrganizationMemberDialog } from '@/composables/useOrganizationMemberDialog'

import { getProtectedImage } from '@/utils/api/fetchAuthImage'
import { forceLogout } from '@/utils/auth/forceLogout'
import { eventEmitter } from '@/utils/events/event-emitter'

import * as aggr from '@/api-generator/aggr'
import { feUserProfileAPI, feCompaniesAPI, feShortUrlsAPI } from '@/APIs'
import { CompanySubscriptionDisplayName } from '@/constant/client/company'
import { CommonEvent } from '@/constant/events'
import { LOCAL_STORAGE_KEYS } from '@/constant/storage'
import { AvatarSize } from '@/constant/user'
import appStore from '@/stores'

const route = useRoute()
const router = useRouter()

const authStore = appStore.useAuthStore
const shortUrlStore = appStore.useShortUrlStore
const orgSortTagStore = appStore.useOrgSortTagStore
const feCompanyStore = appStore.useFECompanyStore
const feUserProfileStore = appStore.useFEUserProfileStore

/** 公司和組織列表 */
const companyAndOrganizationList = ref<aggr.FECompanyGetCompaniesAndOrganizationsRs>()

/** 搜尋關鍵字 */
const searchTerm = ref('')

/** 搜尋載入狀態 */
const isSearching = ref(false)

/** 使用者所屬的公司總數（初始載入時設定，不受搜尋結果影響） */
const totalCompanyCount = ref(0)

/** 公司組織圖片 */
const companyImgUrl = ref('')

/** 取得並設定使用者電話號碼 */
const fetchAndSetUserPhoneNumber = async () => {
  try {
    const { data } = await feUserProfileAPI.getUserPhoneNumber()
    feUserProfileStore.setUserPhoneNumber(data.phone)
  } catch (error) {
    console.error(error)
  }
}

/** 取得公司和組織清單 */
const handleGetCompaniesAndOrganizations = async (term?: string) => {
  try {
    isSearching.value = true
    const { data } = await feCompaniesAPI.getCompaniesAndOrganizations(term)
    companyAndOrganizationList.value = data
    return data
  } catch (error) {
    console.error(error)
    return null
  } finally {
    isSearching.value = false
  }
}

/** 處理搜尋事件 - 使用當前搜尋關鍵字 */
const handleSearch = () => {
  handleGetCompaniesAndOrganizations(searchTerm.value)
}

/** 清空搜尋資料 - 立即執行，避免顯示舊資料 */
const handleClearSearchData = () => {
  companyAndOrganizationList.value = undefined
}

/** 取得公司圖片 */
const setCompanyBlobImg = async () => {
  const blobImg = await getProtectedImage(feCompanyStore.feOrganizations?.companyThumbUrl)
  companyImgUrl.value = blobImg
}

/** 取得組織受眾標籤 */
const fetchOrgSubTags = async () => {
  try {
    const { data } = await feShortUrlsAPI.querySubTags(
      shortUrlStore.selectedCompanyUuId,
      shortUrlStore.selectedOrganizationId
    )
    shortUrlStore.setSubTags(data)
  } catch (error) {
    console.error(error)
  }
}

/** 取得當前組織的分類標記列表 */
const fetchOrgSortTags = async () => {
  await orgSortTagStore.fetchOrgSortTags({
    companyUuId: shortUrlStore.selectedCompanyUuId,
    companyOrganizationUuid: shortUrlStore.selectedOrganizationId
  })
}

/** 更新路由查詢參數 */
const updateRouteQuery = (
  companyUuid: string,
  orgId: string,
  urlUid?: string,
  mainCode?: string,
  subTagId?: string,
  subTag1Id?: string,
  subTag2Id?: string,
  startDate?: string | undefined,
  endDate?: string | undefined
) => {
  // 如果不在列表頁面，跳轉到「網址列表」頁面
  const allowedListRoutes = ['shorter-list', 'group-list', 'tag-stats']
  const targetRouteName = route.name && allowedListRoutes.includes(route.name as string) ? route.name : 'shorter-list'

  router.replace({
    name: targetRouteName,
    query: {
      companyUuid,
      orgId,
      urlUid,
      mainCode,
      subTagId,
      subTag1Id,
      subTag2Id,
      startDate,
      endDate
    }
  })
}

const openConfirmUserLogoutDialog = () => {
  isUserDropdownVisible.value = false
  const { confirm } = useConfirmDialog()
  confirm({
    title: '是否登出',
    text: '確定要登出嗎？',
    confirmButtonText: '確定',
    onConfirm: async () => {
      await forceLogout()
    }
  })
}

const navigateToAdmin = () => {
  shortUrlStore.setOrganizationId('')
  router.push({ name: 'admin' })
}

const navigateToSingleList = () => {
  router.push({
    name: 'shorter-list',
    query: { companyUuid: shortUrlStore.selectedCompanyUuId, orgId: shortUrlStore.selectedOrganizationId }
  })
}

// 使用者下拉選單
const target = ref<HTMLDivElement | null>(null)
const isUserDropdownVisible = ref(false)
const toggleSystemSwitch = (event: MouseEvent) => {
  event.stopPropagation()
  isUserDropdownVisible.value = !isUserDropdownVisible.value
}

onClickOutside(target, () => {
  isUserDropdownVisible.value = false
})

const subscriptionClass = ref('')

const formatSubPlanName = (planName: CompanySubscriptionDisplayName) => {
  switch (planName) {
    case CompanySubscriptionDisplayName.Professional:
      subscriptionClass.value = 'bg-[#25272A]'
      return 'Pro'
    case CompanySubscriptionDisplayName.Enterprise:
      subscriptionClass.value = 'bg-[#1c7af3]'
      return 'Ent'
    case CompanySubscriptionDisplayName.Basic:
      subscriptionClass.value = 'hidden'
      return ''
    case CompanySubscriptionDisplayName.Trial:
      subscriptionClass.value = 'hidden'
      return ''
    default:
      return ''
  }
}

const formattedSubPlanName = computed(() => {
  if (!feCompanyStore.feOrganizations) {
    return ''
  }
  return formatSubPlanName(feCompanyStore.feOrganizations?.subscriptionDisplayName as CompanySubscriptionDisplayName)
})

const isSystemStaff = computed(() => !!authStore.userInfo?.is_system_staff)

const selectedOrganization = computed(() => {
  if (!feCompanyStore.feOrganizations) {
    return null
  }
  return (
    feCompanyStore.feOrganizations?.options?.find(
      (org) => org.companyOrganizationUuid === shortUrlStore.selectedOrganizationId
    ) || null
  )
})

const { openOrganizationMemberDialog } = useOrganizationMemberDialog()

const handleAddMember = () => {
  const organization = {
    companyUuid: shortUrlStore.selectedCompanyUuId,
    companyOrganizationUuid: shortUrlStore.selectedOrganizationId,
    companyOrganizationName: selectedOrganization.value?.companyOrganizationName || '未知組織',
    organizationDetail: []
  }

  openOrganizationMemberDialog({
    organizationData: organization,
    isDisplayedOnFrontend: true
  })
}

/** 處理選擇公司
 * @param companyUuid 公司 UUID
 */
const handleSelectCompany = (companyUuid: string) => {
  shortUrlStore.setCompanyUuId(companyUuid)
  localStorage.setItem(LOCAL_STORAGE_KEYS.COMPANY_UUID, companyUuid)
}

/** 處理選擇組織
 * @param organizationUuid 組織 UUID
 */
const handleSelectOrganization = async (organizationUuid: string) => {
  shortUrlStore.setOrganizationId(organizationUuid)

  localStorage.setItem(LOCAL_STORAGE_KEYS.COMPANY_UUID, shortUrlStore.selectedCompanyUuId)
  localStorage.setItem(LOCAL_STORAGE_KEYS.ORG_ID, organizationUuid)

  updateRouteQuery(
    shortUrlStore.selectedCompanyUuId,
    organizationUuid,
    route.query.urlUid as string,
    route.query.mainCode as string,
    route.query.subTagId as string,
    route.query.subTag1Id as string,
    route.query.subTag2Id as string,
    route.query.startDate as string | undefined,
    route.query.endDate as string | undefined
  )

  await Promise.allSettled([fetchOrgSubTags(), fetchOrgSortTags()])
  eventEmitter.emit(CommonEvent.REFRESH_LIST)
}

/** 開啟設定個人資料彈窗 */
const openProfilePanelDialog = async () => {
  await fetchAndSetUserPhoneNumber()
  isUserDropdownVisible.value = false
  eventEmitter.emit(CommonEvent.OPEN_DIALOG, {
    title: '個人資料設定',
    children: () => h(ProfilePanelDialogContent, { modelValue: true, 'onUpdate:modelValue': () => {} }),
    dialogProps: {
      width: '844px',
      top: '8vh'
    }
  })
}

onMounted(async () => {
  if (feCompanyStore.feOrganizations?.subscription) {
    feCompanyStore.setCompanySubPlan(feCompanyStore.feOrganizations.subscription)
    setCompanyBlobImg()
  }
  await authStore.getUserInfo()
  const initData = await handleGetCompaniesAndOrganizations()
  totalCompanyCount.value = initData?.companyCount || 0
  await Promise.allSettled([authStore.setUserBlobAvatar(AvatarSize.SMALL), fetchOrgSubTags(), fetchOrgSortTags()])
})
</script>
<template>
  <header
    class="flex h-20 items-center justify-between border-b border-solid border-b-app-black-100 bg-app-primary-blue-10 py-4 pl-4 pr-4 md:pl-9 md:pr-10"
  >
    <div class="flex min-w-0 items-center">
      <a class="hidden h-12 cursor-pointer md:block" aria-label="回到首頁" @click.prevent="navigateToSingleList">
        <SVGIcon name="logo-icon" width="163px" height="48px" />
      </a>
      <div class="ml-6 mr-2 hidden h-8 w-0.5 bg-app-black-100 md:block"></div>
      <CompanyOrganizationSelector
        v-model:term="searchTerm"
        :company-and-organization-list="companyAndOrganizationList"
        :is-system-staff="isSystemStaff"
        :selected-company-uuid="shortUrlStore.selectedCompanyUuId"
        :selected-organization-uuid="shortUrlStore.selectedOrganizationId"
        :is-loading="isSearching"
        :total-company-count="totalCompanyCount"
        @select-company="handleSelectCompany"
        @select-organization="handleSelectOrganization"
        @search="handleSearch"
        @clear-search-data="handleClearSearchData"
      />
    </div>
    <div class="flex items-center gap-2">
      <button
        v-if="selectedOrganization?.hasEditPermissions"
        type="button"
        class="h-12 w-12 rounded-full bg-white p-[14px] text-app-black-400 hover:text-app-black-600"
        @click="handleAddMember"
      >
        <Icon icon="ri:user-add-line" width="20px" height="20px" />
      </button>
      <div ref="target">
        <div class="h-12 w-12 cursor-pointer transition-colors hover:bg-white/10">
          <UserAvatar class="avatar" :size="48" :src="authStore.userSMAvatarUrl" @click.stop="toggleSystemSwitch" />
        </div>
        <div
          v-if="isUserDropdownVisible"
          class="dropdown-backdrop-filter absolute right-10 top-[76px] z-[99999] overflow-hidden rounded-[10px] bg-app-black-900/85"
        >
          <div class="p-4">
            <div class="mb-3 h-4 w-8 rounded-full px-2 text-center text-[11px] text-white" :class="subscriptionClass">
              {{ formattedSubPlanName || '' }}
            </div>
            <div class="mb-1 text-base text-white">{{ authStore.userInfo?.name }}</div>
            <div class="mb-3 text-xs text-app-black-300">{{ authStore.userInfo?.email }}</div>
            <button
              type="button"
              class="flex h-8 w-full items-center justify-center rounded-xl bg-[#46474a]"
              @click="openProfilePanelDialog"
            >
              <span class="text-xs text-white">設定個人資料</span>
            </button>
          </div>

          <ul class="flex w-[240px] flex-col">
            <li v-if="authStore.isAdmin" class="border-t border-solid border-app-black-700">
              <button type="button" class="user-dropdown-btn" @click="navigateToAdmin">
                <Icon icon="ri:tools-line" width="16px" height="16px" style="color: #a5abb3" />
                <span class="text-sm text-white">系統設定</span>
              </button>
            </li>
            <li class="border-t border-solid border-app-black-700" @click="openConfirmUserLogoutDialog">
              <button type="button" class="user-dropdown-btn">
                <Icon icon="ri:logout-box-r-line" width="16px" height="16px" style="color: #a5abb3" />
                <span class="text-sm text-white">登出</span>
              </button>
            </li>
          </ul>
        </div>
      </div>
      <!-- <div>
        <ProfilePanel v-model="profilePanelVisible" />
      </div> -->
    </div>
  </header>
</template>

<style scoped>
.user-dropdown-btn {
  @apply flex h-full w-full items-center gap-2 p-4 transition-colors hover:bg-[#46474a];
}

.dropdown-backdrop-filter {
  backdrop-filter: blur(16.399999618530273px);
}
</style>
